import { useNamedHeader } from 'hooks/useNamedHeader';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	editModifierGroupsInitialState,
	EditModifierGroupsContext,
	TVisibleForm,
	TTypeOfModalWindow,
} from './editModifierGroupsContext';
import { editModifierGroupsReducer } from './editModifierGroupsReducer';
import { EditModifierGroupsActionTypes } from './editModifierGroupsActions';
import { useFetchData } from 'hooks/useFetchData';
import { getModifierGroupsInfo } from 'backend/modifierGroups';
import {
	ICreateModifierGroupValidationError,
	IItemForGetShortInfo,
	IServerError,
	TLanguageIDs,
	IDefaultLanguage,
	IGetModifierGroupsInfo,
	IUpdateModifierGroups,
	IDishesByRestaurantIdsAndSubcategoryIdsItem,
} from 'types';

const EditModifierGroupsState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editModifierGroupsReducer, editModifierGroupsInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const modifierGroupsID = params.get('modifierGroupsID');

	const { response, loading, refetch } = useFetchData<IGetModifierGroupsInfo, string>(
		modifierGroupsID ? getModifierGroupsInfo : null,
		modifierGroupsID || '',
	);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditModifierGroupsActionTypes.REFETCH_DATA,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useNamedHeader(response?.data.modifierGroup.name);

	useEffect(() => {
		if (response) {
			const unionCategories = response.data.modifierGroup.dishes.map((el) => el.categoryIds).flat();
			const uniqueCategories = Array.from(new Set(unionCategories));
			const unionSubcategories = response.data.modifierGroup.dishes.map((el) => el.subCategoryIds).flat();
			const uniqueSubcategories = Array.from(new Set(unionSubcategories));
			dispatch({
				type: EditModifierGroupsActionTypes.SET_ALL_DATA,
				payload: {
					modifierGroupsID: modifierGroupsID ? modifierGroupsID : '',
					initialIDSelectedCustomer: response.data.modifierGroup.customerId,
					initialIDsSelectedRestaurant: response.data.modifierGroup.restaurantsModifierGroups.map(
						(el) => el.restaurantId,
					),
					IDsIsForceDisabledRestaurant: response.data.modifierGroup.restaurantsModifierGroups
						.filter((el) => el.forceDisable)
						.map((el) => el.restaurantId),
					initialIDsSelectedCategories: uniqueCategories,
					initialIDsSelectedSubcategories: uniqueSubcategories,
					initialIDsSelectedDishes: response.data.modifierGroup.dishes.map((el) => el.id),
					nameOfModifierGroup: response.data.modifierGroup.name ? response.data.modifierGroup.name : '',
					modifiersOfModifierGroups: response.data.modifierGroup.modifiers,
					externalId: response.data.modifierGroup.externalId || '',
					articleNumber: response.data.modifierGroup.articleNumber,
					translations: response.data.modifierGroup.translations,
				},
			});
			dispatch({
				type: EditModifierGroupsActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						IDSelectedCustomer: response.data.modifierGroup.customerId,
						IDsSelectedRestaurant: response.data.modifierGroup.restaurantsModifierGroups.map(
							(el) => el.restaurantId,
						),
						IDsIsForceDisabledRestaurant: response.data.modifierGroup.restaurantsModifierGroups
							.filter((el) => el.forceDisable)
							.map((el) => el.restaurantId),
						IDsSelectedDishes: response.data.modifierGroup.dishes.map((el) => el.id),
						nameOfModifierGroup: response.data.modifierGroup.name || '',
						externalId: response.data.modifierGroup.externalId || '',
						translations: response.data.modifierGroup.translations,
					},
				},
			});
			// Reset restaurants after refetch on update data (see isFirstRender condition in restaurants select)
			if (state.IDsSelectedRestaurant.length && response) {
				dispatch({
					type: EditModifierGroupsActionTypes.SET_IDS_RESTAURANT,
					payload: {
						IDsSelectedRestaurant: response.data.modifierGroup.restaurantsModifierGroups.map(
							(el) => el.restaurantId,
						),
						IDsIsForceDisabledRestaurant: response.data.modifierGroup.restaurantsModifierGroups
							.filter((el) => el.forceDisable)
							.map((el) => el.restaurantId),
					},
				});
			}
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditModifierGroupsActionTypes.REFETCH_DATA,
			payload: {
				needRefetch: value,
			},
		});
	}, []);

	const changeLoaderStatus = useCallback((value: boolean) => {
		dispatch({
			type: EditModifierGroupsActionTypes.CHANGE_LOADER_STATUS,
			payload: {
				isLoading: value,
			},
		});
	}, []);

	const changeVisibleForm = useCallback((value: TVisibleForm) => {
		dispatch({
			type: EditModifierGroupsActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditModifierGroupsActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				customers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditModifierGroupsActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditModifierGroupsActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setIDsRestaurant = useCallback(
		(value: string[]) => {
			const filteredIDsIsForceDisabledRestaurant = state.IDsIsForceDisabledRestaurant.filter((el) =>
				value.includes(el),
			);

			dispatch({
				type: EditModifierGroupsActionTypes.SET_IDS_RESTAURANT,
				payload: {
					IDsSelectedRestaurant: value,
					IDsIsForceDisabledRestaurant: filteredIDsIsForceDisabledRestaurant,
				},
			});
		},
		[state.IDsIsForceDisabledRestaurant],
	);

	const setIDsIsForceDisabledRestaurant = useCallback(
		(value: string) => {
			if (state.IDsIsForceDisabledRestaurant.includes(value)) {
				const filteredIDsIsForceDisabledRestaurant = state.IDsIsForceDisabledRestaurant.filter(
					(el) => el !== value,
				);
				dispatch({
					type: EditModifierGroupsActionTypes.SET_IDS_IS_FORCE_DISABLED_RESTAURANT,
					payload: {
						IDsIsForceDisabledRestaurant: filteredIDsIsForceDisabledRestaurant,
					},
				});
			} else {
				dispatch({
					type: EditModifierGroupsActionTypes.SET_IDS_IS_FORCE_DISABLED_RESTAURANT,
					payload: {
						IDsIsForceDisabledRestaurant: [...state.IDsIsForceDisabledRestaurant, value],
					},
				});
			}
		},
		[state.IDsIsForceDisabledRestaurant],
	);

	const setFetchedCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditModifierGroupsActionTypes.SET_FETCHED_CATEGORIES,
			payload: {
				fetchedCategories: value,
			},
		});
	}, []);

	const setIDsCategories = useCallback((value: string[]) => {
		dispatch({
			type: EditModifierGroupsActionTypes.SET_IDS_CATEGORIES,
			payload: {
				IDsSelectedCategories: value,
			},
		});
	}, []);

	const updateCategories = useCallback(
		(value: IItemForGetShortInfo[]) => {
			const IDsOfCategoriesShortInfo = value.map((el) => el.id);
			const updatedSelectedCategories = state.IDsSelectedCategories.filter((el) =>
				IDsOfCategoriesShortInfo.includes(el),
			);
			dispatch({
				type: EditModifierGroupsActionTypes.SET_IDS_CATEGORIES,
				payload: {
					IDsSelectedCategories: updatedSelectedCategories,
				},
			});
		},
		[state.IDsSelectedCategories],
	);

	const setFetchedSubcategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditModifierGroupsActionTypes.SET_FETCHED_SUBCATEGORIES,
			payload: {
				fetchedSubcategories: value,
			},
		});
	}, []);

	const setIDsSubcategories = useCallback((value: string[]) => {
		dispatch({
			type: EditModifierGroupsActionTypes.SET_IDS_SUBCATEGORIES,
			payload: {
				IDsSelectedSubcategories: value,
			},
		});
	}, []);

	const updateSubcategories = useCallback(
		(value: IItemForGetShortInfo[]) => {
			const IDsOfSubcategoriesShortInfo = value.map((el) => el.id);
			const updatedSelectedSubcategories = state.IDsSelectedSubcategories.filter((el) =>
				IDsOfSubcategoriesShortInfo.includes(el),
			);
			dispatch({
				type: EditModifierGroupsActionTypes.SET_IDS_SUBCATEGORIES,
				payload: {
					IDsSelectedSubcategories: updatedSelectedSubcategories,
				},
			});
		},
		[state.IDsSelectedSubcategories],
	);

	const setFetchedDishes = useCallback((value: IDishesByRestaurantIdsAndSubcategoryIdsItem[]) => {
		dispatch({
			type: EditModifierGroupsActionTypes.SET_FETCHED_DISHES,
			payload: {
				dishes: value,
			},
		});
	}, []);

	const setIDsDishes = useCallback((value: string[]) => {
		dispatch({
			type: EditModifierGroupsActionTypes.SET_IDS_DISHES,
			payload: {
				IDsSelectedDishes: value,
			},
		});
	}, []);

	const updateDishes = useCallback(
		(value: IDishesByRestaurantIdsAndSubcategoryIdsItem[]) => {
			const IDsOfDishesShortInfo = value.map((el) => el.id);
			const updatedSelectedDishes = state.IDsSelectedDishes.filter((el) => IDsOfDishesShortInfo.includes(el));
			dispatch({
				type: EditModifierGroupsActionTypes.SET_IDS_DISHES,
				payload: {
					IDsSelectedDishes: updatedSelectedDishes,
				},
			});
		},
		[state.IDsSelectedDishes],
	);

	const changeNameOfModifierGroup = useCallback((value: string) => {
		dispatch({
			type: EditModifierGroupsActionTypes.CHANGE_NAME_OF_MODIFIER_GROUP,
			payload: {
				nameOfModifierGroup: value,
			},
		});
	}, []);

	const changeExternalId = useCallback((value: string) => {
		dispatch({
			type: EditModifierGroupsActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: value,
			},
		});
	}, []);

	const generateUniqueExternalId = useCallback(() => {
		const uniqueExternalId = Date.now().toString();
		dispatch({
			type: EditModifierGroupsActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: uniqueExternalId,
			},
		});
	}, []);

	const setDefaultCustomerLanguage = useCallback((value: Nullable<IDefaultLanguage>) => {
		dispatch({
			type: EditModifierGroupsActionTypes.SET_DEFAULT_CUSTOMER_LANGUAGE,
			payload: {
				defaultCustomerLanguage: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: EditModifierGroupsActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditModifierGroupsActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: IUpdateModifierGroups | null) => {
		dispatch({
			type: EditModifierGroupsActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditModifierGroupsActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: IServerError<ICreateModifierGroupValidationError> | null) => {
		dispatch({
			type: EditModifierGroupsActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: EditModifierGroupsActionTypes.RESET_FIELDS }), []);

	return (
		<EditModifierGroupsContext.Provider
			value={{
				refetchData,
				changeLoaderStatus,
				changeVisibleForm,
				setFetchedCustomers,
				setIDCustomer,
				setFetchedRestaurants,
				setIDsRestaurant,
				setIDsIsForceDisabledRestaurant,
				setFetchedCategories,
				setIDsCategories,
				updateCategories,
				setFetchedSubcategories,
				setIDsSubcategories,
				updateSubcategories,
				setFetchedDishes,
				setIDsDishes,
				updateDishes,
				changeNameOfModifierGroup,
				changeExternalId,
				generateUniqueExternalId,
				setDefaultCustomerLanguage,
				setTranslationTab,
				changeNameTranslation,
				setDataForSubmit,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				isLoading: state.isLoading,
				modifierGroupsID: state.modifierGroupsID,
				visibleForm: state.visibleForm,
				fetchedCustomers: state.fetchedCustomers,
				initialIDSelectedCustomer: state.initialIDSelectedCustomer,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				initialIDsSelectedRestaurant: state.initialIDsSelectedRestaurant,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				IDsIsForceDisabledRestaurant: state.IDsIsForceDisabledRestaurant,
				fetchedCategories: state.fetchedCategories,
				initialIDsSelectedCategories: state.initialIDsSelectedCategories,
				IDsSelectedCategories: state.IDsSelectedCategories,
				fetchedSubcategories: state.fetchedSubcategories,
				initialIDsSelectedSubcategories: state.initialIDsSelectedSubcategories,
				IDsSelectedSubcategories: state.IDsSelectedSubcategories,
				fetchedDishes: state.fetchedDishes,
				initialIDsSelectedDishes: state.initialIDsSelectedDishes,
				IDsSelectedDishes: state.IDsSelectedDishes,
				nameOfModifierGroup: state.nameOfModifierGroup,
				externalId: state.externalId,
				articleNumber: state.articleNumber,
				modifiersOfModifierGroups: state.modifiersOfModifierGroups,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</EditModifierGroupsContext.Provider>
	);
};

export default EditModifierGroupsState;
